import Image from 'next/image';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  createContext,
  useContext,
} from 'react';
import useEmblaCarousel, { UseEmblaCarouselType } from 'embla-carousel-react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardCopyIcon,
  PlayIcon,
} from '@heroicons/react/outline';
import { IProdView } from '@/lib/types/bevmaq.types';
import { YoutubeVideo } from '../../YoutubeVideo';
import classNames from 'classnames';
import { pauseAllYoutubeVideos } from '@/lib/utilities/client-utils';

interface ThumbnailItem {
  url: string;
  alt?: string;
}

interface ProductImagesContextType {
  thumbnailItems: ThumbnailItem[];
  selectedIndex: number;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number>>;
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  emblaRef: UseEmblaCarouselType[0];
  emblaApi: UseEmblaCarouselType[1] | undefined;
  thumbViewportRef: UseEmblaCarouselType[0];
  emblaThumbs: UseEmblaCarouselType[1] | undefined;
  thumbViewportRefMain: UseEmblaCarouselType[0];
  emblaThumbsMain: UseEmblaCarouselType[1] | undefined;
  onThumbClick: (index: number) => void;
  onThumbClickArrow: (direction: 'left' | 'right') => void;
  onSelect: () => void;
}

const ProductImagesContext = createContext<ProductImagesContextType | null>(
  null
);

function useProductImagesContext() {
  const context = useContext(ProductImagesContext);
  if (!context) {
    throw new Error(
      'useProductImagesContext must be used within a ProductImagesContextProvider'
    );
  }
  return context;
}

function ArrowButton({
  direction,
  onClick,
  className = '',
}: {
  direction: 'left' | 'right';
  onClick: () => void;
  className?: string;
}) {
  const isLeft = direction === 'left';
  return (
    <button
      className={classNames(
        `embla__button embla__button--${
          isLeft ? 'prev' : 'next'
        }  flex h-auto flex-col justify-center bg-black bg-opacity-50 p-2`,
        className
      )}
      onClick={onClick}
      type='button'
    >
      {isLeft ? (
        <ChevronLeftIcon className='h-6 w-6 text-white' />
      ) : (
        <ChevronRightIcon className='h-6 w-6 text-white' />
      )}
    </button>
  );
}

function ProductImageSliderSelector({ isModal = false }) {
  const {
    thumbnailItems,
    onThumbClick,
    selectedIndex,
    thumbViewportRef,
    thumbViewportRefMain,
    isModalOpen,
  } = useProductImagesContext();
  const thumbRef = isModal ? thumbViewportRef : thumbViewportRefMain;
  const backgroundClass = isModalOpen ? 'bg-gray2-950 justify-center' : 'mt-4';
  return (
    <div className={`flex space-x-1 ${backgroundClass}`}>
      <div className='embla--thumb max-w-full space-x-2 overflow-hidden'>
        <div className='embla__viewport' ref={thumbRef}>
          <div className='embla__container--thumb flex'>
            {thumbnailItems.map((item, index) => (
              <div
                key={item.url}
                className={classNames(`relative`, {
                  'is-selected': index === selectedIndex,
                  'pl-3': index !== 0,
                })}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onThumbClick(index);
                  }}
                  className='relative m-0 block h-24 w-full overflow-hidden bg-transparent p-0 outline-none'
                  type='button'
                >
                  <div
                    className={classNames('relative h-24 w-24', {
                      'border-4 border-orange-600': selectedIndex === index,
                    })}
                  >
                    <Thumbnail item={item} />
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
function ProductImagesModal() {
  const {
    isModalOpen,
    setIsModalOpen,
    emblaRef,
    emblaApi,
    selectedIndex,
    thumbnailItems,
    onSelect,
  } = useProductImagesContext();

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onSelect);
  }, [emblaApi, onSelect]);

  if (!isModalOpen) return null;

  return (
    <div className='fixed inset-0 z-50 bg-[#000000ad]'>
      <div
        className='relative flex h-full w-full flex-col overflow-hidden p-0'
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Black Bar */}
        <div className='flex justify-end bg-black p-2'>
          <button className='flex-1 ' onClick={() => setIsModalOpen(false)} />
          <button
            className='btn btn-circle tooltip tooltip-left btn-ghost btn-sm p-1 text-white'
            data-tip='Clipboard URL'
            onClick={async () => {
              try {
                await navigator?.clipboard?.writeText(
                  thumbnailItems[selectedIndex].url
                );
              } catch (e) {
                console.log(e);
              }
            }}
          >
            <ClipboardCopyIcon className='rounded-full' />
          </button>

          <button
            id='closer'
            className='btn btn-circle btn-sm border-none bg-primary text-white'
            onClick={() => setIsModalOpen(false)}
          >
            ✕
          </button>
        </div>

        {/* Main Content */}
        <div className='relative flex h-full w-full flex-col'>
          {/* Carousel Section */}
          <div className='relative h-full w-full bg-black pb-4'>
            <div className='relative h-full w-full' ref={emblaRef}>
              <div className='mb-4 flex h-full'>
                {thumbnailItems.map((i) => (
                  <div
                    className='relative h-full flex-shrink-0 flex-grow-0 basis-full'
                    key={i.url}
                  >
                    <CarouselItem item={i} />
                  </div>
                ))}
              </div>
            </div>

            {/* Arrow Buttons */}
            <ArrowButton
              direction='left'
              onClick={() => emblaApi && emblaApi.scrollPrev()}
              className='absolute left-4 top-1/2 z-10 -translate-y-1/2 transform'
            />
            <ArrowButton
              direction='right'
              onClick={() => emblaApi && emblaApi.scrollNext()}
              className='absolute right-4 top-1/2 z-10 -translate-y-1/2 transform'
            />
          </div>

          {/* Thumbnail Selector */}
          <div className='relative h-28 w-full bg-gray-900'>
            <ProductImageSliderSelector isModal={true} />
          </div>
        </div>
      </div>
    </div>
  );
}

interface ProductImageProps {
  product: IProdView;
}

export function ProductImages({ product }: ProductImageProps) {
  const thumbnailItems = useMemo(
    () => [...product.images, ...product.videos].filter((img) => img.url),
    [product.images, product.videos]
  );
  const initialSelectedIndex = thumbnailItems.findIndex(
    (item) => item.url === product.thumbnail_image
  );

  const [selectedIndex, setSelectedIndex] = useState(
    initialSelectedIndex >= 0 ? initialSelectedIndex : 0
  );

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: true,
    slidesToScroll: 1,
    skipSnaps: false,
    speed: 20,
  });

  const [thumbViewportRef, emblaThumbs] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });

  const [thumbViewportRefMain, emblaThumbsMain] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  const onThumbClick = useCallback(
    (index: number) => {
      const newIndex = (index + thumbnailItems.length) % thumbnailItems.length;
      emblaThumbsMain?.scrollTo(newIndex);
      setSelectedIndex(newIndex);

      if (!emblaApi || !emblaThumbs) return;
      if (emblaThumbs.clickAllowed()) {
        emblaApi.scrollTo(newIndex);
        emblaThumbs.scrollTo(newIndex);
      }
    },
    [emblaApi, emblaThumbs, emblaThumbsMain, thumbnailItems]
  );

  const onThumbClickArrow = useCallback(
    (direction: 'right' | 'left') => {
      const delta = direction === 'right' ? 1 : -1;
      onThumbClick(selectedIndex + delta);
    },
    [onThumbClick, selectedIndex]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi || !emblaThumbs) return;
    const index = emblaApi.selectedScrollSnap();
    setSelectedIndex(index);
    emblaThumbs.scrollTo(index);
  }, [emblaApi, emblaThumbs]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
  }, [emblaApi, onSelect]);

  useEffect(() => {
    pauseAllYoutubeVideos();
  }, [isModalOpen]);

  useEffect(() => {
    const newIndex = thumbnailItems.findIndex(
      (item) => item.url === product.thumbnail_image
    );
    setSelectedIndex(newIndex >= 0 ? newIndex : 0);
  }, [product, thumbnailItems]);

  useDisableScrollOnModalOpen(isModalOpen);
  const contextValue: ProductImagesContextType = useMemo(
    () => ({
      thumbnailItems,
      selectedIndex,
      setSelectedIndex,
      isModalOpen,
      setIsModalOpen,
      emblaRef,
      emblaApi,
      thumbViewportRef,
      emblaThumbs,
      thumbViewportRefMain,
      emblaThumbsMain,
      onThumbClick,
      onThumbClickArrow,
      onSelect,
    }),
    [
      thumbnailItems,
      selectedIndex,
      isModalOpen,
      emblaRef,
      emblaApi,
      thumbViewportRef,
      emblaThumbs,
      thumbViewportRefMain,
      emblaThumbsMain,
      onThumbClick,
      onThumbClickArrow,
      onSelect,
    ]
  );
  return (
    <ProductImagesContext.Provider value={contextValue}>
      <div
        className='flex max-w-full flex-col items-start overflow-hidden md:col-span-2 md:mb-12'
        id='product-images'
      >
        <ProductMainImage />
        <div className='relative w-full'>
          <ProductImageSliderSelector />
        </div>
        <ProductImagesModal />
      </div>
    </ProductImagesContext.Provider>
  );
}

function useDisableScrollOnModalOpen(isModalOpen: boolean) {
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = isModalOpen ? 'hidden' : originalStyle;
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, [isModalOpen]);
}

function isYoutubeUrl(url: string): boolean {
  return /youtu/.test(url);
}

function Thumbnail({ item }: { item: ThumbnailItem }) {
  const isVideo = isYoutubeUrl(item.url);
  const [isHidden, setIsHidden] = useState(false);
  const { isModalOpen } = useProductImagesContext();

  return isVideo ? (
    <div
      className={`h-full w-full cursor-pointer overflow-hidden hover:ring-2 hover:ring-primary ${
        isModalOpen ? 'bg-black' : ''
      }`}
    >
      <div className='relative flex h-full w-full items-center justify-center bg-red-900'>
        <PlayIcon className='w-14 text-white' />
      </div>
    </div>
  ) : (
    <div
      className={`h-full w-full cursor-pointer overflow-hidden hover:ring-2 hover:ring-primary ${
        isHidden ? 'hidden' : ''
      } ${isModalOpen ? 'bg-black' : ''}`}
    >
      <div className='relative h-full w-full'>
        <Image
          src={item.url}
          alt={item.alt ?? 'Picture of the product'}
          fill
          className='object-cover object-center'
          onError={() => setIsHidden(true)}
        />
      </div>
    </div>
  );
}

function CarouselItem({ item }: { item: ThumbnailItem }) {
  return isYoutubeUrl(item.url) ? (
    <YoutubeVideo url={item.url} />
  ) : (
    <div className='relative h-full w-full'>
      <Image
        src={item.url}
        alt={item.alt ?? 'Machine image'}
        fill
        className='h-full w-full cursor-grab object-contain object-center'
      />
    </div>
  );
}

function ProductMainImage() {
  const { thumbnailItems, selectedIndex, setIsModalOpen, onThumbClickArrow } =
    useProductImagesContext();
  const mainImageSrc = thumbnailItems[selectedIndex]?.url;
  const isYouTube = mainImageSrc.includes('youtu');

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  return (
    <div className='aspect-h-1 aspect-w-1 w-full cursor-grab space-y-4 overflow-hidden p-0 '>
      <div className='relative h-[18em] w-full md:h-[35rem] '>
        <div className='relative h-full w-full'>
          <ArrowButton
            direction='left'
            onClick={() => onThumbClickArrow('left')}
            className='absolute left-0 top-1/2 z-10 -translate-y-1/2 transform'
          />
          {mainImageSrc && isYouTube ? (
            <YoutubeVideo url={mainImageSrc} />
          ) : (
            <button
              className='relative h-full w-full'
              onClick={handleOpenModal}
            >
              <Image
                src={mainImageSrc}
                alt='Product Image'
                fill
                className='w-full object-cover object-left'
                priority={true}
              />
            </button>
          )}
          <ArrowButton
            direction='right'
            onClick={() => onThumbClickArrow('right')}
            className='absolute right-0 top-1/2 z-10 -translate-y-1/2 transform'
          />
        </div>
      </div>
    </div>
  );
}
